import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { DEFAULT_REMINDER, localTz } from "@/hooks/useReminders";
import { HabitAndTime, DayPicker, ChannelPicker, EscalationSettings, ALL_DAYS, inputCls } from "@/components/reminders/ReminderEditorParts";

const SUGGEST_TOAST_MS = 6000;

export function ReminderEditor({ habits, initial, channelSetup = {}, onSave, onCancel }) {
  const [f, setF] = useState(initial || { ...DEFAULT_REMINDER, habit_id: habits[0]?.id || "", timezone: localTz() });
  const [suggesting, setSuggesting] = useState(false);
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }));
  const setEsc = (k, v) => setF((cur) => ({ ...cur, escalation: { ...cur.escalation, [k]: v } }));
  const toggleCh = (k) => setF((cur) => ({ ...cur, channels: { ...cur.channels, [k]: !cur.channels[k] } }));
  const toggleDay = (d) => set("days", f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort());

  const suggestTime = async () => {
    if (!f.habit_id) return;
    setSuggesting(true);
    try {
      const r = await api.get(`/habits/${f.habit_id}/best-time`, { params: { tz: f.timezone || localTz() } });
      if (r.data.suggested_time) {
        set("time", r.data.suggested_time);
        toast.success(`${r.data.message} Set to ${r.data.suggested_time}.`, { duration: SUGGEST_TOAST_MS });
      } else {
        toast(r.data.message);
      }
    } finally {
      setSuggesting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.habit_id || f.days.length === 0) return;
    onSave({ ...f, timezone: f.timezone || localTz() });
  };

  return (
    <form onSubmit={submit} className="warm-card p-5 space-y-4" data-testid="reminder-editor">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-amber-700" />
        <h3 className="font-display font-bold text-stone-900">{initial?.id ? "Edit reminder" : "New reminder"}</h3>
      </div>
      <HabitAndTime f={f} habits={habits} onChange={set} onSuggest={suggestTime} suggesting={suggesting} />
      <DayPicker days={f.days} onToggle={toggleDay} onAll={() => set("days", ALL_DAYS)} />
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" data-testid="reminder-smart" checked={f.smart} onChange={(e) => set("smart", e.target.checked)} className="mt-0.5 accent-amber-600" />
        <span><span className="font-semibold text-stone-800">Smart</span> — only remind me if I haven't checked in yet that day</span>
      </label>
      <input data-testid="reminder-message" value={f.message} onChange={(e) => set("message", e.target.value)} maxLength={200}
        placeholder="Optional message to yourself (e.g. 'Shoes are by the door')" className={inputCls + " w-full"} />
      <ChannelPicker channels={f.channels} channelSetup={channelSetup} onToggle={toggleCh} />
      <EscalationSettings esc={f.escalation} onChange={setEsc} />
      <div className="flex gap-2">
        <button data-testid="reminder-save" type="submit" className="amber-btn text-sm">Save reminder</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-semibold bg-stone-100 text-stone-700">Cancel</button>
      </div>
    </form>
  );
}
